import RocketIcon from "@redotech/redo-icons/rocket-02.svg";
import { JSXElementConstructor, memo, MouseEvent } from "react";
import { RedoButton } from "../buttons/redo-button";
import {
  FeaturedIconColor,
  FeaturedIconType,
  RedoFeaturedIcon,
} from "../featured-icon/redo-featured-icon";
import { Flex } from "../flex";
import { Text } from "../text";
import * as redoEmptyStateCss from "./redo-empty-state.module.css";

export interface EmptyStateButtonProps {
  "data-dd-action-name"?: string;
  onClick: (event?: MouseEvent<HTMLButtonElement>) => void;
  text: string;
  dataTestId?: string;
}

/**
 * Arbiter empty state https://www.figma.com/design/iZHj2I36zd9i8nRbWKw4ZK/%E2%9D%96-Arbiter?node-id=1547-269979&t=sTAJa3mMkpmOVvjw-0
 */
export const RedoEmptyState = memo(function RedoEmptyState({
  text,
  Icon,
  iconColor,
  iconType,
  subtext,
  primaryButtonProps,
  secondaryButtonProps,
  useNonArbiterPadding,
}: {
  text: string;
  Icon?: JSXElementConstructor<any>;
  iconColor?: FeaturedIconColor | undefined;
  iconType?: FeaturedIconType | undefined;
  subtext: string;
  primaryButtonProps?: EmptyStateButtonProps;
  secondaryButtonProps?: EmptyStateButtonProps;
  /**
   *
   * This component was made with padding that isn't there in Arbiter.
   * To avoid breaking the hundred existing uses of it, this flag lets
   * a caller keep the non-Arbiter padding.
   *
   * @deprecated This flag should NOT be used in new usages of this
   * component. Instead, if padding is needed, the caller should wrap
   * RedoEmptyState in a Flex with the desired padding.
   */
  useNonArbiterPadding?: boolean;
}) {
  return (
    <Flex
      align="center"
      data-arbiter="RedoEmptyState"
      dir="column"
      gap="xs"
      grow={1}
      justify="flex-start"
      {...(useNonArbiterPadding ? { px: "4xl", py: "8xl" } : {})}
      style={{ width: "100%" }}
    >
      <RedoFeaturedIcon
        color={iconColor}
        Icon={Icon || RocketIcon}
        size="lg"
        type={iconType}
      />
      <Text
        fontSize="md"
        fontWeight="semibold"
        pt="md"
        textAlign="center"
        textColor="primary"
      >
        {text}
      </Text>
      <Text
        className={redoEmptyStateCss.subtext}
        fontSize="sm"
        textColor="secondary"
      >
        {subtext}
      </Text>
      <Flex dir="row" gap="lg" pt="xl">
        {secondaryButtonProps && (
          <RedoButton
            centerItems={false}
            data-dd-action-name={secondaryButtonProps["data-dd-action-name"]}
            dataTestId={secondaryButtonProps.dataTestId}
            hierarchy="secondary"
            onClick={secondaryButtonProps.onClick}
            size="sm"
            text={secondaryButtonProps.text}
          />
        )}
        {primaryButtonProps && (
          <RedoButton
            centerItems={false}
            data-dd-action-name={primaryButtonProps["data-dd-action-name"]}
            dataTestId={primaryButtonProps.dataTestId}
            hierarchy="primary"
            onClick={primaryButtonProps.onClick}
            size="sm"
            text={primaryButtonProps.text}
          />
        )}
      </Flex>
    </Flex>
  );
});
